import { FOOTPRINT_CELL_SPAN, MAX_FOOTPRINT_CELLS } from "../document/footprint-cells.ts";
import { isEmptyFrameDepth, type FrameDepth } from "./ground-depth.ts";

/**
 * What a library entry's cells measure (ADR-113 decision 7): an entry says the side in pixels of its `cells` and its
 * `passage` together in `cell`, and an entry that says nothing holds the authoring cell, so everything stored before
 * the field is read as it always was.
 */

/**
 * The side in pixels of an entry's cells when it names none: the authoring cell.
 */
export const FOOTPRINT_DEFAULT_CELL = 8;

/**
 * The side in pixels of the finer cell an entry may name: the compiled cell, half the authoring one.
 */
export const FOOTPRINT_FINE_CELL = 4;

/**
 * Every side in pixels an entry's `cell` may hold.
 */
export const FOOTPRINT_CELLS: readonly number[] = [FOOTPRINT_DEFAULT_CELL, FOOTPRINT_FINE_CELL];

/**
 * Why a value is no entry cell.
 *
 * @param value The candidate.
 * @returns The reason, or undefined when the value is a side an entry may name.
 */
export function footprintCellIssue(value: unknown): string | undefined {
  return typeof value === "number" && FOOTPRINT_CELLS.includes(value) ? undefined : `cell must be ${FOOTPRINT_DEFAULT_CELL} or ${FOOTPRINT_FINE_CELL} pixels`;
}

/**
 * The most cells an entry holds and the coordinate its cells stay under, on the same pixel area at either size: the
 * authoring cell's 4,096 cells and span of 1,024 (`MAX_FOOTPRINT_CELLS`, `FOOTPRINT_CELL_SPAN`) scaled by how many of
 * these cells fit in one of them.
 *
 * @param cell The cell's side in pixels.
 * @returns The most cells and the span.
 */
export function footprintCellLimits(cell: number): { readonly span: number; readonly count: number } {
  const ratio = FOOTPRINT_DEFAULT_CELL / cell;
  return { span: FOOTPRINT_CELL_SPAN * ratio, count: MAX_FOOTPRINT_CELLS * ratio * ratio };
}

/**
 * The side in pixels of the cell a frame's ground is cut at: the finer of its entry's cell and its painted depth's, so
 * cells drawn finer give a finer sort and Walk on and Walk under cells drawn finer than the footprint read the
 * footprint at their size. A depth that paints nothing counts as the authoring cell.
 *
 * @param entry The entry's `cell`, or undefined when it names none.
 * @param depth The entry's depth, or undefined when it has none.
 * @param authoring The authoring cell in pixels.
 * @returns The cell in pixels.
 */
export function groundCellOf(entry: number | undefined, depth: FrameDepth | undefined, authoring: number = FOOTPRINT_DEFAULT_CELL): number {
  const painted = depth !== undefined && !isEmptyFrameDepth(depth);
  return Math.min(entry ?? authoring, painted ? (depth.cell ?? authoring) : authoring);
}

/**
 * A depth in finer cells. A finer cell divides a coarser one exactly, so a rectangle becomes the same pixels in
 * finer cells and nothing about it changes. The result names its cell unless it is the authoring one.
 *
 * @param depth The depth.
 * @param from The side in pixels its rectangles are counted in.
 * @param to The side in pixels they are wanted in, which divides `from`.
 * @param authoring The authoring cell in pixels.
 * @returns The depth, the same object when the sides agree.
 * @throws RangeError when `to` is coarser than `from` or does not divide it.
 */
export function depthAtCell(depth: FrameDepth, from: number, to: number, authoring: number = FOOTPRINT_DEFAULT_CELL): FrameDepth {
  if (from === to) {
    return depth;
  }
  if (to > from || from % to !== 0) {
    throw new RangeError(`a depth in ${from} px cells cannot be exactly counted in ${to} px cells`);
  }
  const factor = from / to;
  const scale = (box: readonly [number, number, number, number]): [number, number, number, number] => [box[0] * factor, box[1] * factor, box[2] * factor, box[3] * factor];
  const { walkOn, walkUnder } = depth;
  return { ...(walkOn === undefined ? {} : { walkOn: walkOn.map(scale) }), ...(walkUnder === undefined ? {} : { walkUnder: walkUnder.map(scale) }), ...(to === authoring ? {} : { cell: to }) };
}

/**
 * A depth without the cell it names, for comparing two depths already counted in one cell.
 *
 * @param depth The depth.
 * @returns The depth with only its rectangles.
 */
export function depthWithoutCell(depth: FrameDepth): FrameDepth {
  const { cell: _cell, ...rest } = depth;
  return rest;
}
